import { Arena } from '../entities/Arena';
import { Paddle } from '../entities/Paddle';
import { Ball } from '../entities/Ball';
import { PowerUp, PowerUpType } from '../entities/PowerUp';
import { BlackHole } from '../entities/BlackHole';
import { ParticleSystem } from './ParticleSystem';
import { PALETTE } from '../config/colors';

export class CanvasRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Failed to acquire 2D canvas context');
    }
    this.ctx = context;
  }

  private logicalWidth: number = 0;
  private logicalHeight: number = 0;
  private dpr: number = 1;

  resize(width: number, height: number): void {
    this.logicalWidth = width;
    this.logicalHeight = height;
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(width * this.dpr);
    this.canvas.height = Math.round(height * this.dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
  }

  render(
    arena: Arena,
    paddles: Paddle[],
    balls: Ball[],
    powerups: PowerUp[],
    blackHole: BlackHole,
    particles: ParticleSystem,
    frameCounter: number,
    isCountingDown: boolean,
    countdownValue: number,
    canvasMessage: string
  ): void {
    const ctx = this.ctx;
    const width = this.logicalWidth || this.canvas.width;
    const height = this.logicalHeight || this.canvas.height;
    const center = arena.center;

    ctx.save();
    ctx.scale(this.dpr, this.dpr);

    // 1. Background Radial Gradient
    const safeRadius = Math.max(1, arena.radius * 1.6);
    const grad = ctx.createRadialGradient(center.x, center.y, 0, center.x, center.y, safeRadius);
    grad.addColorStop(0, '#151520');
    grad.addColorStop(1, PALETTE.background);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    ctx.save();

    // 2. Screen Shake
    if (particles.shakeAmount > 0.5) {
      const dx = (Math.random() - 0.5) * particles.shakeAmount;
      const dy = (Math.random() - 0.5) * particles.shakeAmount;
      ctx.translate(dx, dy);
    }

    // 3. Black Hole
    if (blackHole.active) {
      const pulse = Math.sin(frameCounter * 0.1) * 5;
      const bhGrad = ctx.createRadialGradient(
        blackHole.pos.x,
        blackHole.pos.y,
        0,
        blackHole.pos.x,
        blackHole.pos.y,
        blackHole.radius + pulse
      );
      bhGrad.addColorStop(0, '#000000');
      bhGrad.addColorStop(0.4, 'rgba(170, 0, 255, 0.8)');
      bhGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');

      ctx.beginPath();
      ctx.arc(blackHole.pos.x, blackHole.pos.y, blackHole.radius + pulse, 0, Math.PI * 2);
      ctx.fillStyle = bhGrad;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(blackHole.pos.x, blackHole.pos.y, blackHole.radius * 0.35, 0, Math.PI * 2);
      ctx.fillStyle = '#0a0a0f';
      ctx.shadowBlur = 20;
      ctx.shadowColor = '#aa00ff';
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    const numSides = paddles.length;
    const edges = arena.getAllEdges(numSides);

    // 4. Arena Edges (Highlighted if hit)
    for (let i = 0; i < numSides; i++) {
      const edge = edges[i];
      const isHit = i === arena.hitLineIndex && arena.hitLineTimer > 0;

      ctx.beginPath();
      ctx.moveTo(edge.p1.x, edge.p1.y);
      ctx.lineTo(edge.p2.x, edge.p2.y);

      if (isHit) {
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3;
        ctx.shadowBlur = 25;
        ctx.shadowColor = arena.lastKillerColor;
      } else {
        const pad = paddles[i];
        ctx.strokeStyle = pad && pad.isWall ? PALETTE.wallDuel : PALETTE.wallBorder;
        ctx.lineWidth = 2;
        ctx.shadowBlur = 0;
      }

      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // 5. PowerUps
    for (const pu of powerups) {
      ctx.save();
      ctx.translate(pu.pos.x, pu.pos.y);
      ctx.rotate(pu.angle);

      if (pu.life < 60) {
        ctx.globalAlpha = pu.life / 60;
      }

      ctx.fillStyle = pu.color;
      ctx.shadowBlur = 15;
      ctx.shadowColor = pu.color;

      if (pu.type === PowerUpType.ONE_UP) {
        ctx.fillRect(-10, -4, 20, 8);
        ctx.fillRect(-4, -10, 8, 20);
      } else if (pu.type === PowerUpType.MULTIBALL) {
        ctx.beginPath();
        ctx.arc(0, -8, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(-8, 5, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(8, 5, 5, 0, Math.PI * 2);
        ctx.fill();
      } else if (pu.type === PowerUpType.SHIELD) {
        ctx.beginPath();
        ctx.moveTo(0, -12);
        ctx.lineTo(10, 0);
        ctx.lineTo(0, 12);
        ctx.lineTo(-10, 0);
        ctx.fill();
      } else if (pu.type === PowerUpType.EMP_FREEZE) {
        ctx.lineWidth = 3;
        ctx.strokeStyle = pu.color;
        ctx.beginPath();
        ctx.moveTo(-10, 0);
        ctx.lineTo(10, 0);
        ctx.moveTo(0, -10);
        ctx.lineTo(0, 10);
        ctx.moveTo(-7, -7);
        ctx.lineTo(7, 7);
        ctx.moveTo(-7, 7);
        ctx.lineTo(7, -7);
        ctx.stroke();
      }

      ctx.restore();
    }

    // 6. Ripples & Particles (Render under paddles and balls)
    particles.draw(ctx);

    // 7. Paddles and Shields
    for (let i = 0; i < numSides; i++) {
      const pad = paddles[i];
      const edge = edges[i];

      // Draw Shield Forcefield if active
      if (pad.shieldTimer > 0) {
        const shieldAlpha = Math.min(1, pad.shieldTimer / 30);
        ctx.beginPath();
        ctx.moveTo(edge.p1.x, edge.p1.y);
        ctx.lineTo(edge.p2.x, edge.p2.y);
        ctx.strokeStyle = `rgba(0, 170, 255, ${shieldAlpha})`;
        ctx.lineWidth = 4;
        ctx.shadowBlur = 15;
        ctx.shadowColor = '#00aaff';
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      const padPhys = pad.getPhysicalEndpoints(edge.p1, edge.p2, edge.faceAngle);

      ctx.beginPath();
      ctx.moveTo(padPhys.start.x, padPhys.start.y);
      ctx.lineTo(padPhys.end.x, padPhys.end.y);

      if (pad.isWall) {
        ctx.strokeStyle = pad.color;
        ctx.lineWidth = 3;
      } else {
        let pulseMod = 0;
        let glowMod = 0;
        if (pad.lives === 1) {
          pulseMod = Math.sin(frameCounter * 0.15) * 3;
          glowMod = Math.sin(frameCounter * 0.15) * 10;
        }

        if (pad.freezeTimer > 0) {
          ctx.strokeStyle = frameCounter % 10 < 5 ? '#00ffff' : pad.color;
          ctx.lineWidth = Math.max(1, 6 + pulseMod);
          ctx.shadowBlur = 15;
          ctx.shadowColor = '#00ffff';
        } else if (pad.smashActiveTicks > 0) {
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = Math.max(1, 10 + pulseMod);
          ctx.shadowBlur = Math.max(0, 20 + glowMod);
          ctx.shadowColor = '#ffffff';
        } else {
          ctx.strokeStyle = pad.color;
          ctx.lineWidth = Math.max(1, 6 + pulseMod);
          ctx.shadowBlur = Math.max(0, 10 + glowMod);
          ctx.shadowColor = pad.color;
        }
        ctx.lineCap = 'round';
      }

      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // 8. Balls & Glowing Trails
    for (const b of balls) {
      if (b.isDead) continue;

      if (b.trail.length > 0) {
        for (let i = 0; i < b.trail.length; i++) {
          const pt = b.trail[i];
          const opacity = i / b.trail.length;
          const size = b.radius * opacity;

          ctx.beginPath();
          ctx.arc(pt.pos.x, pt.pos.y, size, 0, Math.PI * 2);
          ctx.globalAlpha = opacity * 0.6;
          ctx.fillStyle = pt.color;
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;
      }

      // Chromatic glow when ball is blazing fast (smashed or gold sweet spot)
      if (b.speed > 10 && (b.color === '#ffffff' || b.color === '#ffcc00')) {
        ctx.fillStyle = 'rgba(255, 0, 85, 0.7)';
        ctx.beginPath();
        ctx.arc(b.pos.x - b.vel.x * 0.5, b.pos.y - b.vel.y * 0.5, b.radius, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = 'rgba(0, 170, 255, 0.7)';
        ctx.beginPath();
        ctx.arc(b.pos.x + b.vel.x * 0.3, b.pos.y + b.vel.y * 0.3, b.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(b.pos.x, b.pos.y, b.radius, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 20;
      ctx.shadowColor = b.color;
      ctx.fill();
      ctx.shadowBlur = 0;
    }

    // 9. In-World Smash Charge Bar (Below Human Paddle)
    const human = paddles.find(p => p.isHuman);
    if (human && !isCountingDown) {
      const barWidth = 240;
      const barHeight = 14;
      const barX = center.x - barWidth / 2;
      const idealBarY = center.y + arena.radius + 45;
      const maxBarY = height - barHeight - (human.smashCharge >= 1.0 ? 32 : 12);
      const barY = Math.min(idealBarY, maxBarY);

      ctx.strokeStyle = 'rgba(0, 255, 204, 0.3)';
      ctx.lineWidth = 2;
      ctx.strokeRect(barX, barY, barWidth, barHeight);

      const numSegments = 10;
      const gap = 2;
      const totalGaps = (numSegments - 1) * gap;
      const segmentWidth = (barWidth - totalGaps) / numSegments;
      const activeSegments = Math.floor(human.smashCharge * numSegments);

      for (let s = 0; s < numSegments; s++) {
        const sX = barX + s * (segmentWidth + gap);

        if (s < activeSegments) {
          if (human.smashCharge >= 1.0) {
            ctx.fillStyle = '#ffffff';
            ctx.shadowBlur = 15;
            ctx.shadowColor = '#ffffff';
          } else {
            ctx.fillStyle = '#00ffcc';
            ctx.shadowBlur = 0;
          }
        } else {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
          ctx.shadowBlur = 0;
        }

        ctx.fillRect(sX, barY, segmentWidth, barHeight);
      }
      ctx.shadowBlur = 0;

      if (human.smashCharge >= 1.0) {
        ctx.fillStyle = '#ffffff';
        ctx.font = "bold 13px 'Rajdhani', 'Courier New', monospace";
        ctx.textAlign = 'center';
        ctx.shadowBlur = 10;
        ctx.shadowColor = '#00ffcc';
        ctx.fillText('⚡ READY TO SMASH [W] ⚡', center.x, barY + barHeight + 20);
        ctx.shadowBlur = 0;
      }
    }

    ctx.restore();

    // 10. In-Arena Countdown & Announcements
    if (isCountingDown) {
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      ctx.fillStyle = '#ff0055';
      ctx.font = width < 600 ? "bold 24px 'Rajdhani', monospace" : "bold 32px 'Rajdhani', monospace";
      ctx.shadowBlur = 15;
      ctx.shadowColor = '#ff0055';

      const lines = canvasMessage.split('\n');
      const lineHeight = width < 600 ? 28 : 36;

      lines.forEach((line, index) => {
        const yOffset = (index - (lines.length - 1) / 2) * lineHeight;
        ctx.fillText(line, center.x, center.y - 120 + yOffset);
      });

      // Big countdown number
      ctx.fillStyle = '#00ffcc';
      ctx.font = width < 600 ? "bold 90px 'Rajdhani', monospace" : "bold 130px 'Rajdhani', monospace";
      ctx.shadowBlur = 30;
      ctx.shadowColor = '#00ffcc';
      ctx.fillText(countdownValue.toString(), center.x, center.y + 90);

      ctx.restore();
    }

    ctx.restore(); // Restore DPR scaling
  }
}
